import {t} from '@lingui/macro';
import _, {groupBy} from 'lodash';

import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {LocalizeRenderer} from '../../../srcCommon/internationalization/internationalizationContext';
import {CompanyPainPointsEnum} from '../../models/api/companyPreferencesModel';

type Article = {
  title: LocalizeRenderer;
  description: LocalizeRenderer;
  link: string;
  color: PaletteColorsEnum;
  icon: IconName;
};

type ArticlesColumn = {
  title: LocalizeRenderer;
  articles: ReadonlyArray<Article>;
};

type PainPointsArticleMap = {[k in CompanyPainPointsEnum]: ArticlesColumn};
const painPointsToArticlesColumnsMap: PainPointsArticleMap = {
  [CompanyPainPointsEnum.SLOW_RESPONSE_TIME]: {
    title: (localize) => localize(t('speed-up-response-times')`Speed up response times`),
    articles: [
      {
        color: PaletteColorsEnum.ORANGE,
        icon: 'snoozeFilledSmall',
        title: (localize) =>
          localize(t('service-level-agreement-rules-sl-as')`Service-level agreement rules (SLAs)`),
        description: (localize) =>
          localize(
            t(
              'set-alerts-to-meet-time-limits-on-response-times-and-ensure-happy-customers',
            )`Set alerts to meet time limits on response times and ensure happy customers.`,
          ),
        link: 'https://help.frontapp.com/t/q6hh1bw/sla-rules',
      },
      {
        color: PaletteColorsEnum.BLUE,
        icon: 'assignFilledSmall',
        title: (localize) =>
          localize(t('round-robin-messages-to-the-team')`Round-robin messages to the team`),
        description: (localize) =>
          localize(
            t(
              'evenly-disperse-messages-among-a-group-of-teammates-by-automating-assignments',
            )`Evenly disperse messages among a group of teammates by automating assignments.`,
          ),
        link: 'https://help.frontapp.com/t/h42v6y#assign-in-a-round-robin',
      },
    ],
  },
  [CompanyPainPointsEnum.MISSED_EMAILS]: {
    title: (localize) => localize(t('ensure-emails-are-never-missed')`Ensure emails are never missed`),
    articles: [
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) => localize(t('triage-sender-based-on-customer')`Triage sender based on customer`),
        description: (localize) =>
          localize(
            t(
              'assign-or-move-emails-from-name-client-com-to-automate-triaging-by-customer',
            )`Assign or move emails from name@client.com to automate triaging by customer.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#assign-or-move-based-on-sender',
      },
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) => localize(t('move-or-assign-based-on-content')`Move or assign based on content`),
        description: (localize) =>
          localize(
            t(
              'assign-or-move-messages-based-on-the-content-of-the-message-to-triage-by-expertise',
            )`Assign or move messages based on the content of the message to triage by expertise.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#assign-or-move-based-on-content',
      },
    ],
  },
  [CompanyPainPointsEnum.DUPLICATE_EMAILS]: {
    title: (localize) => localize(t('work-as-a-team')`Work as a team`),
    articles: [
      {
        color: PaletteColorsEnum.BLUE,
        icon: 'assignFilledSmall',
        title: (localize) =>
          localize(
            t('assign-tag-and-prioritize-your-team-s-work')`Assign, tag, and prioritize your team's work`,
          ),
        description: (localize) =>
          localize(
            t(
              'escalate-messages-route-conversations-to-the-right-teammate-and-categorize-communication',
            )`Escalate messages, route conversations to the right teammate, and categorize communication.`,
          ),
        link: 'https://frontapp.com/classroom/manage-customer-messages-with-ease',
      },
      {
        color: PaletteColorsEnum.BLUE,
        icon: 'assignFilledSmall',
        title: (localize) => localize(t('how-to-collaborate-in-front')`How to collaborate in Front`),
        description: (localize) =>
          localize(
            t(
              'loop-in-a-teammate-with-a-simple-mention-or-share-a-draft-and-write-it-together',
            )`Loop in a teammate with a simple @mention, or share a draft and write it together.`,
          ),
        link: 'https://frontapp.com/classroom/staying-connected-collaboration-in-front',
      },
    ],
  },
  [CompanyPainPointsEnum.INCOMPLETE_VIEW_OF_CUSTOMER_COMMS]: {
    title: (localize) => localize(t('view-customer-info-in-front')`View customer info in Front`),
    articles: [
      {
        color: PaletteColorsEnum.GREY,
        icon: 'integrationsFilledSmall',
        title: (localize) =>
          localize(t('access-salesforce-data-from-front')`Access Salesforce data from Front`),
        description: (localize) =>
          localize(
            t(
              'check-details-and-edit-records-from-salesforce-as-you-reply-to-your-customers',
            )`Check details and edit records from Salesforce as you reply to your customers.`,
          ),
        link: 'https://help.frontapp.com/t/q529hj/how-to-enable-and-use-the-salesforce-integration',
      },
      {
        color: PaletteColorsEnum.GREY,
        icon: 'integrationsFilledSmall',
        title: (localize) => localize(t('explore-other-cr-ms-in-front')`Explore other CRMs in Front`),
        description: (localize) =>
          localize(
            t(
              'add-base-pipedrive-hub-spot-and-more-to-access-what-you-need-directly-in-front',
            )`Add Base, Pipedrive, HubSpot, and more to access what you need directly in Front.`,
          ),
        link: 'https://help.frontapp.com/t/18wrm1/introduction-to-front-integrations',
      },
    ],
  },
  [CompanyPainPointsEnum.EMAIL_OVERLOAD]: {
    title: (localize) => localize(t('avoid-email-overload')`Avoid email overload`),
    articles: [
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) => localize(t('filter-emails-to-other-inboxes')`Filter emails to other inboxes`),
        description: (localize) =>
          localize(
            t(
              'assign-or-move-messages-based-on-the-content-of-the-message-to-triage-by-expertise',
            )`Assign or move messages based on the content of the message to triage by expertise.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#assign-or-move-based-on-content',
      },
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'archiveFilledSmall',
        title: (localize) =>
          localize(t('set-up-rules-to-archive-messages')`Set up rules to archive messages`),
        description: (localize) =>
          localize(
            t(
              'filter-out-messages-that-don-t-require-a-response-by-automatically-archiving-them',
            )`Filter out messages that don’t require a response by automatically archiving them.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#archive-and-tag-based-on-content',
      },
    ],
  },
  [CompanyPainPointsEnum.FRUSTRATED_CUSTOMERS]: {
    title: (localize) => localize(t('reduce-customer-frustration')`Reduce customer frustration`),
    articles: [
      {
        color: PaletteColorsEnum.PINK,
        icon: 'caseStudySmall',
        title: (localize) => localize(t('how-culture-amp-uses-front')`How Culture Amp uses Front`),
        description: (localize) =>
          localize(
            t(
              'culture-amp-s-customer-success-team-uses-front-to-manage-communications-with-customers',
            )`Culture Amps's customer success team uses Front to manage communications with customers.`,
          ),
        link: 'https://front.com/customer-stories/culture-amp',
      },
      {
        color: PaletteColorsEnum.PINK,
        icon: 'caseStudySmall',
        title: (localize) => localize(t('how-zenconnect-uses-front')`How ZENCONNECT uses Front`),
        description: (localize) =>
          localize(
            t(
              'zenconnect-increased-productivity-and-time-savings-by-centralizing-communication',
            )`ZENCONNECT increased productivity and time-savings by centralizing communication.`,
          ),
        link: 'https://frontapp.com/customer-stories/zenconnect',
      },
    ],
  },
  [CompanyPainPointsEnum.LACK_OF_OWNERSHIP]: {
    title: (localize) => localize(t('assign-the-right-teammate')`Assign the right teammate`),
    articles: [
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) => localize(t('move-or-assign-based-on-sender')`Move or assign based on sender`),
        description: (localize) =>
          localize(
            t(
              'assign-or-move-emails-from-name-client-com-to-automate-triaging-by-customer',
            )`Assign or move emails from name@client.com to automate triaging by customer.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#assign-or-move-based-on-sender',
      },
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) => localize(t('move-or-assign-based-on-content')`Move or assign based on content`),
        description: (localize) =>
          localize(
            t(
              'assign-or-move-messages-based-on-the-content-of-the-message-to-triage-by-expertise',
            )`Assign or move messages based on the content of the message to triage by expertise.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#assign-or-move-based-on-content',
      },
    ],
  },
  [CompanyPainPointsEnum.OOO_HANDLING]: {
    title: (localize) => localize(t('cover-people-who-are-ooo')`Cover people who are OOO`),
    articles: [
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) =>
          localize(t('set-yourself-or-your-team-as-ooo')`Set yourself or your team as OOO`),
        description: (localize) =>
          localize(
            t(
              'out-of-office-features-and-vacation-responder-help-your-team-cover-when-a-person-s-ooo',
            )`'Out of Office features and vacation responder help your team cover when a person’s OOO.`,
          ),
        link: 'https://help.frontapp.com/t/k9244v/how-to-handle-being-out-of-office',
      },
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) => localize(t('use-rules-for-ooo-coverage')`Use rules for OOO coverage`),
        description: (localize) =>
          localize(
            t(
              'need-more-transparency-when-a-teammate-is-ooo-route-individual-emails-to-a-team-inbox',
            )`Need more transparency when a teammate is OOO? Route individual emails to a team inbox.`,
          ),
        link: 'https://help.frontapp.com/t/m2298d/out-of-office-rule',
      },
    ],
  },
  [CompanyPainPointsEnum.LACK_OF_EMAIL_CATEGORIZATION]: {
    title: (localize) => localize(t('organize-your-inbox-with-rules')`Organize your inbox with rules`),
    articles: [
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) =>
          localize(t('automate-moving-messages-to-inboxes')`Automate moving messages to inboxes`),
        description: (localize) =>
          localize(
            t(
              'need-help-triaging-your-info-company-com-messages-we-recommend-these-rules',
            )`Need help triaging your info@company.com messages? We recommend these rules.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules',
      },
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'archiveFilledSmall',
        title: (localize) => localize(t('setup-rules-to-archive-messages')`Setup rules to archive messages`),
        description: (localize) =>
          localize(
            t(
              'filter-out-messages-that-don-t-require-a-response-by-automatically-archiving-them',
            )`Filter out messages that don’t require a response by automatically archiving them.`,
          ),
        link: 'https://help.frontapp.com/t/q6hx3y1/common-triage-rules#archive-and-tag-based-on-content',
      },
    ],
  },
  [CompanyPainPointsEnum.LACK_OF_VISIBILITY]: {
    title: (localize) => localize(t('track-team-performance')`Track team performance`),
    articles: [
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'analyticsFilledSmall',
        title: (localize) =>
          localize(t('view-analytics-for-team-performance')`View analytics for team performance`),
        description: (localize) =>
          localize(
            t(
              'get-a-summarized-view-of-your-team-s-email-volume-response-times-and-more',
            )`Get a summarized view of your team’s email volume, response times, and more.`,
          ),
        link: 'https://help.frontapp.com/t/18d74v/webinar-front-analytics',
      },
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'analyticsFilledSmall',
        title: (localize) => localize(t('tag-messages-to-create-reports')`Tag messages to create reports`),
        description: (localize) =>
          localize(
            t(
              'tag-messages-based-on-the-content-assignee-and-more-to-create-custom-analytics-reports',
            )`Tag messages based on the content, assignee, and more to create custom analytics reports.`,
          ),
        link: 'https://help.frontapp.com/t/h42v6y#add-a-tag',
      },
    ],
  },
  [CompanyPainPointsEnum.POOR_NOTIFICATIONS]: {
    title: (localize) =>
      localize(t('get-notified-about-important-messages')`Get notified about important messages`),
    articles: [
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) =>
          localize(t('setup-advanced-push-notifications')`Setup advanced push notifications`),
        description: (localize) =>
          localize(
            t(
              'get-notified-when-new-messages-arrive-in-team-inboxes-you-re-mentioned-and-more',
            )`Get notified when new messages arrive in team inboxes, you’re @mentioned, and more.`,
          ),
        link: 'https://help.frontapp.com/t/6329qd/how-to-change-my-notification-settings',
      },
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) =>
          localize(t('manage-your-team-s-notifications')`Manage your team’s notifications`),
        description: (localize) =>
          localize(
            t(
              'help-your-team-onboard-by-setting-up-their-notifications-so-they-know-what-s-important',
            )`Help your team onboard by setting up their notifications so they know what’s important.`,
          ),
        link: 'https://help.frontapp.com/t/36b5xn/how-admins-can-manage-teammate-settings#notifications',
      },
    ],
  },
  [CompanyPainPointsEnum.INEFFICIENT_NON_EXISTANT_COLLABORATION]: {
    title: (localize) =>
      localize(t('tackle-customer-responses-together')`Tackle customer responses together`),
    articles: [
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) => localize(t('share-drafts-with-your-team')`Share drafts with your team`),
        description: (localize) =>
          localize(
            t(
              'review-drafts-and-get-feedback-from-your-teammates-to-ensure-accurate-responses',
            )`Review drafts and get feedback from your teammates to ensure accurate responses.`,
          ),
        link: 'https://help.frontapp.com/t/1824vd/how-to-share-drafts-and-edit-them-together',
      },
      {
        color: PaletteColorsEnum.YELLOW,
        icon: 'groupSmall',
        title: (localize) => localize(t('set-up-shared-tags-and-more')`Set up shared tags and more`),
        description: (localize) =>
          localize(
            t(
              'unify-your-workflow-by-creating-shared-tags-signatures-and-more-for-your-team',
            )`Unify your workflow by creating shared tags, signatures, and more for your team.`,
          ),
        link: 'https://help.frontapp.com/t/m1h38y3/setting-up-your-teams-workflow-team-tags-signatures-rules-and-canned-responses',
      },
    ],
  },
  [CompanyPainPointsEnum.SILOED_EMAILS]: {
    title: (localize) =>
      localize(t('don-t-lose-messages-in-one-person-s-inbox')`Don’t lose messages in one person’s inbox`),
    articles: [
      {
        color: PaletteColorsEnum.PURPLE,
        icon: 'moveRightFilledSmall',
        title: (localize) =>
          localize(t('move-individual-emails-to-team-inboxes')`Move individual emails to team inboxes`),
        description: (localize) =>
          localize(
            t(
              'need-more-transparency-around-individual-inboxes-move-certain-emails-to-a-team-inbox',
            )`Need more transparency around individual inboxes? Move certain emails to a team inbox.`,
          ),
        link: 'https://help.frontapp.com/t/p8hxxrl/move-individual-emails-to-team-inboxes',
      },
      {
        color: PaletteColorsEnum.BLUE,
        icon: 'assignFilledSmall',
        title: (localize) =>
          localize(t('delegation-for-full-transparency')`Delegation for full transparency`),
        description: (localize) =>
          localize(
            t(
              'need-your-team-to-cover-for-you-share-your-individual-inbox-using-delegations',
            )`Need your team to cover for you? Share your individual inbox using delegations.`,
          ),
        link: 'https://help.frontapp.com/t/80dv6c/how-to-delegate-your-inbox-to-a-teammate',
      },
    ],
  },
  [CompanyPainPointsEnum.NO_SLA_RESPONSE_TIME_TRACKING]: {
    title: (localize) => localize(t('meet-response-time-and-sla-goals')`Meet response time and SLA goals`),
    articles: [
      {
        color: PaletteColorsEnum.ORANGE,
        icon: 'snoozeFilledSmall',
        title: (localize) =>
          localize(t('service-level-agreement-rules-sl-as')`Service-level agreement rules (SLAs)`),
        description: (localize) =>
          localize(
            t(
              'set-alerts-to-meet-time-limits-on-response-times-and-ensure-happy-customers',
            )`Set alerts to meet time limits on response times and ensure happy customers.`,
          ),
        link: 'https://help.frontapp.com/t/q6hh1bw/sla-rules',
      },
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'analyticsFilledSmall',
        title: (localize) => localize(t('tag-messages-to-create-reports')`Tag messages to create reports`),
        description: (localize) =>
          localize(
            t(
              'tag-messages-based-on-the-content-assignee-and-more-to-create-custom-analytics-reports',
            )`Tag messages based on the content, assignee, and more to create custom analytics reports.`,
          ),
        link: 'https://help.frontapp.com/t/h42v6y#add-a-tag',
      },
    ],
  },
  [CompanyPainPointsEnum.NO_SENSE_OF_ROI_FOR_COMM]: {
    title: (localize) => localize(t('understand-team-performance')`Understand team performance`),
    articles: [
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'analyticsFilledSmall',
        title: (localize) =>
          localize(t('view-analytics-for-team-performance')`View analytics for team performance`),
        description: (localize) =>
          localize(
            t(
              'get-a-summarized-view-of-your-team-s-email-volume-response-times-and-more',
            )`Get a summarized view of your team’s email volume, response times, and more.`,
          ),
        link: 'https://help.frontapp.com/t/18d74v/webinar-front-analytics',
      },
      {
        color: PaletteColorsEnum.GREEN,
        icon: 'analyticsFilledSmall',
        title: (localize) => localize(t('understanding-team-analytics')`Understanding team analytics`),
        description: (localize) =>
          localize(
            t(
              'learn-more-about-which-metrics-you-can-track-to-ensure-the-best-customer-experience',
            )`Learn more about which metrics you can track to ensure the best customer experience.`,
          ),
        link: 'https://help.frontapp.com/category/analytics-glossary',
      },
    ],
  },
};

const generalCategories = [
  {
    points: [
      CompanyPainPointsEnum.SLOW_RESPONSE_TIME,
      CompanyPainPointsEnum.MISSED_EMAILS,
      CompanyPainPointsEnum.DUPLICATE_EMAILS,
      CompanyPainPointsEnum.INCOMPLETE_VIEW_OF_CUSTOMER_COMMS,
      CompanyPainPointsEnum.EMAIL_OVERLOAD,
      CompanyPainPointsEnum.FRUSTRATED_CUSTOMERS,
    ],
    extras: [],
  },
  {
    points: [
      CompanyPainPointsEnum.LACK_OF_OWNERSHIP,
      CompanyPainPointsEnum.OOO_HANDLING,
      CompanyPainPointsEnum.LACK_OF_EMAIL_CATEGORIZATION,
      CompanyPainPointsEnum.LACK_OF_VISIBILITY,
      CompanyPainPointsEnum.POOR_NOTIFICATIONS,
    ],
    extras: [CompanyPainPointsEnum.NO_SLA_RESPONSE_TIME_TRACKING],
  },
  {
    points: [
      CompanyPainPointsEnum.INEFFICIENT_NON_EXISTANT_COLLABORATION,
      CompanyPainPointsEnum.SILOED_EMAILS,
    ],
    extras: [
      CompanyPainPointsEnum.LACK_OF_OWNERSHIP,
      CompanyPainPointsEnum.OOO_HANDLING,
      CompanyPainPointsEnum.SLOW_RESPONSE_TIME,
      CompanyPainPointsEnum.LACK_OF_EMAIL_CATEGORIZATION,
    ],
  },
  {
    points: [
      CompanyPainPointsEnum.NO_SLA_RESPONSE_TIME_TRACKING,
      CompanyPainPointsEnum.NO_SENSE_OF_ROI_FOR_COMM,
    ],
    extras: [
      CompanyPainPointsEnum.FRUSTRATED_CUSTOMERS,
      CompanyPainPointsEnum.LACK_OF_OWNERSHIP,
      CompanyPainPointsEnum.OOO_HANDLING,
      CompanyPainPointsEnum.LACK_OF_EMAIL_CATEGORIZATION,
    ],
  },
];
const neededColumns = 6;
export function getArticlesColumnsFromPainPoints(
  painPoints: ReadonlyArray<CompanyPainPointsEnum>,
): ReadonlyArray<ArticlesColumn> {
  return painPoints.map((p) => painPointsToArticlesColumnsMap[p]);
}
export function getFullPainPointsCollection(
  painPoints: ReadonlyArray<CompanyPainPointsEnum>,
): ReadonlyArray<CompanyPainPointsEnum> {
  // Nothing was given, return a default set.
  if (painPoints.length === 0) {
    return generalCategories[0].points;
  }

  // If we're given exactly six, then simply map the columns.
  if (painPoints.length === neededColumns) {
    return painPoints;
  }

  // Group pain points per categories.
  const categoriesMap = groupBy(painPoints, (point) =>
    generalCategories.findIndex((list) => list.points.includes(point)),
  );
  const groups = Object.keys(categoriesMap).map((k) => ({
    category: Number(k),
    points: categoriesMap[k],
  }));

  if (painPoints.length > neededColumns) {
    // Find the largest group.
    const largestGroupSize = _.chain(groups)
      .map((i) => i.points.length)
      .sortBy((i) => -i)
      .value()[0];

    const result: CompanyPainPointsEnum[] = [];

    // Iterate over the size of that group and grab the corresponding pain points.
    for (let index = 0; index < largestGroupSize; index++) {
      const itemsFromEachGroup = _.chain(groups)
        .map((g) => g.points[index])
        .compact()
        .value();

      result.push(...itemsFromEachGroup);
    }

    return result.slice(0, neededColumns);
  }

  // Find the most relevant category (used to fill up if necessary).
  const topCategory = _.chain(groups)
    .sortBy((i) => -i.points.length)
    .value()[0];

  const basePainPoints = painPoints;

  // Grab extra pain points from the top category, excluding the ones we already have.
  const fillupPainPoints = generalCategories[topCategory.category].points
    .filter((p) => !basePainPoints.includes(p))
    .slice(0, neededColumns - basePainPoints.length);
  const currentAmount = basePainPoints.length + fillupPainPoints.length;

  // If we still don't have enough pain points, we need to dig in the extras.
  if (currentAmount < neededColumns) {
    const potentialFillup = generalCategories[topCategory.category].extras.filter(
      (p) => !basePainPoints.includes(p),
    );
    fillupPainPoints.push(...potentialFillup.slice(0, neededColumns - currentAmount));
  }

  // Return the final set.
  return [...basePainPoints, ...fillupPainPoints];
}

export function getUserArticles(): ReadonlyArray<Article> {
  return [
    {
      color: PaletteColorsEnum.RED,
      icon: 'tagFilledSmall',
      title: (localize) => localize(t('organize-your-work-with-tags')`Organize your work with tags`),
      description: (localize) =>
        localize(
          t(
            'private-tags-replace-personal-folders-add-tags-to-your-sidebar-to-easily-find-what-you-need',
          )`Private tags replace personal folders. Add tags to your sidebar to easily find what you need.`,
        ),
      link: 'https://help.frontapp.com/t/h4244c/understanding-tags',
    },
    {
      color: PaletteColorsEnum.GREY,
      icon: 'gearFilledSmall',
      title: (localize) =>
        localize(t('improve-your-productivity-in-front')`Improve your productivity in Front`),
      description: (localize) =>
        localize(
          t(
            'snooze-messages-control-your-notifications-and-make-your-work-day-work-for-you',
          )`Snooze messages, control your notifications, and make your work day work for you.`,
        ),
      link: 'https://frontapp.com/classroom/Individual-inbox-productivity-in-front',
    },
    {
      color: PaletteColorsEnum.TEAL,
      icon: 'messageTemplateFilledSmall',
      title: (localize) => localize(t('all-about-shared-inboxes')`All about shared inboxes`),
      description: (localize) =>
        localize(
          t(
            'learn-how-to-take-action-in-shared-inboxes-and-improve-your-team-s-efficiency',
          )`Learn how to take action in shared inboxes and improve your team's efficiency.`,
        ),
      link: 'https://frontapp.com/classroom/set-up-a-team-inbox',
    },
  ];
}
